import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography
} from '@material-ui/core'
import { Form, Formik } from 'formik'
import { Eye, EyeOff, MailCheck, Send, Trash2 } from 'lucide-react'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import toastError from '../../errors/toastError'
import usePlans from '../../hooks/usePlans'
import api from '../../services/api'
import { ButtonPrimary } from '../ButtonPrimary'
import { ButtonSecondary } from '../ButtonSecondary'
import { InputField } from '../InputField'
import { InputFieldText } from '../InputFieldText'
import { InputSwitch } from '../InputSwitch'
import SelectInput from '../SelectInput'
import { UserPhoneCountry } from '../UserPhoneCountry'
import { useStyles } from './style'

const formatToBRL = value => {
  if (value === undefined || value === null || value === '') return ''
  const numericValue = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(numericValue)) return ''
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericValue)
}

const companyValidationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .min(3, 'O nome da empresa deve ter pelo menos 3 caracteres')
    .required('O nome da empresa é obrigatório'),
  email: Yup.string()
    .trim()
    .email('Insira um e-mail comercial válido')
    .required('O e-mail da empresa é obrigatório'),
  resposavel: Yup.string()
    .trim()
    .required('O nome do responsável é obrigatório'),
  planId: Yup.string().required('A seleção de um plano é obrigatória'),
  recurrence: Yup.string().required('A recorrência do plano é obrigatória'),
  dueDate: Yup.string().required('A data de vencimento é obrigatória'),

  adminName: Yup.string().when('id', {
    is: undefined,
    then: schema =>
      schema.trim().required('O nome do administrador é obrigatório')
  }),
  adminEmail: Yup.string().when('id', {
    is: undefined,
    then: schema =>
      schema
        .trim()
        .email('Insira um e-mail válido')
        .required('O e-mail do administrador é obrigatório')
  }),
  adminPassword: Yup.string().when('id', {
    is: undefined,
    then: schema =>
      schema
        .min(6, 'A senha deve conter no mínimo 6 caracteres')
        .required('A senha do administrador é obrigatória')
  })
})

export function CompanyModal(props) {
  const {
    open,
    onClose,
    onDelete,
    onSubmit,
    onEmailInUse,
    initialValue,
    loading,
    onSendAccessLink
  } = props

  const classes = useStyles()
  const [plans, setPlans] = useState([])
  const { list: listPlans } = usePlans()
  const [companyCountryCode, setCompanyCountryCode] = useState('+55')
  const [adminCountryCode, setAdminCountryCode] = useState('+55')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showAdminPassword, setShowAdminPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [sendingLink, setSendingLink] = useState(false)

  const defaultRecord = {
    name: '',
    email: '',
    phone: '',
    resposavel: '',
    planId: '',
    invoiceValue: '',
    dueDate: '',
    recurrence: '',
    status: true,
    campaignsEnabled: false,
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    adminPhone: '',
    adminCountryCode: '55'
  }

  const [record, setRecord] = useState(defaultRecord)

  useEffect(() => {
    async function fetchData() {
      try {
        const list = await listPlans()
        setPlans(list)

        if (initialValue && initialValue.planId) {
          const selectedPlan = list.find(
            p => String(p.id) === String(initialValue.planId)
          )
          if (selectedPlan && selectedPlan.value) {
            setRecord(prev => ({
              ...prev,
              invoiceValue: selectedPlan.value
            }))
          }
        }
      } catch (e) {
        console.error(e)
      }
    }
    fetchData()
  }, [initialValue])

  useEffect(() => {
    if (initialValue && open) {
      const updatedInit = { ...initialValue }

      if (updatedInit.dueDate && moment(updatedInit.dueDate).isValid()) {
        updatedInit.dueDate = moment(updatedInit.dueDate).format('YYYY-MM-DD')
      } else {
        updatedInit.dueDate = ''
      }
      if (updatedInit.countryCode) {
        setCompanyCountryCode(
          `+${String(updatedInit.countryCode).replace(/\D/g, '')}`
        )
      } else {
        setCompanyCountryCode('+55')
      }

      const parseToFrontPhone = (phone, countryCode) => {
        if (!phone) return ''
        const clean = phone.replace(/\D/g, '')
        const cleanDDI = String(countryCode).replace(/\D/g, '')

        if ((cleanDDI === '55' || cleanDDI === '') && clean.length === 10) {
          return clean.substring(0, 2) + '9' + clean.substring(2)
        }
        return clean
      }

      if (updatedInit.id !== undefined) {
        updatedInit.adminName = updatedInit.adminName || ''
        updatedInit.adminEmail = updatedInit.adminEmail || ''
        updatedInit.adminPassword = ''
        updatedInit.phone = parseToFrontPhone(
          updatedInit.phone,
          updatedInit.countryCode || '55'
        )
        updatedInit.telefone = parseToFrontPhone(
          updatedInit.telefone,
          updatedInit.countryCode || '55'
        )
        if (updatedInit.adminCountryCode) {
          setAdminCountryCode(
            `+${String(updatedInit.adminCountryCode).replace(/\D/g, '')}`
          )
        } else {
          setAdminCountryCode('+55')
        }
        updatedInit.adminPhone = parseToFrontPhone(
          updatedInit.adminPhone,
          updatedInit.adminCountryCode || '55'
        )
      }

      setRecord({
        ...defaultRecord,
        ...updatedInit
      })
    } else if (!open) {
      setRecord(defaultRecord)
      setAdminCountryCode('+55')
      setConfirmPassword('')
    }
  }, [initialValue, open])

  const handleSubmit = async values => {
    const isBrazilCompany = companyCountryCode.replace(/\D/g, '') === '55'
    const isBrazilAdmin = adminCountryCode.replace(/\D/g, '') === '55'

    if (values.phone) {
      const companyPhoneLen = values.phone.replace(/\D/g, '').length
      if (isBrazilCompany && companyPhoneLen !== 11 && companyPhoneLen !== 10) {
        toast.error(
          'O telefone da empresa deve conter 10 ou 11 dígitos (com DDD).'
        )
        throw new Error('Invalid company phone length')
      }
    }

    if (values.id === undefined) {
      if (!values.adminPhone) {
        toast.error('O telefone do administrador é obrigatório.')
        throw new Error('Admin phone is required')
      }
      const adminPhoneLen = values.adminPhone.replace(/\D/g, '').length
      if (isBrazilAdmin && adminPhoneLen !== 11 && adminPhoneLen !== 10) {
        toast.error(
          'O telefone do administrador deve conter 10 ou 11 dígitos (com DDD).'
        )
        throw new Error('Invalid admin phone length')
      }

      if (!values.adminPassword || values.adminPassword.trim() === '') {
        toast.error('A senha do administrador é obrigatória.')
        throw new Error('Password is required')
      }
      if (values.adminPassword !== confirmPassword) {
        toast.error('As senhas digitadas não coincidem.')
        throw new Error('Passwords do not match')
      }
    }

    const formatPhone = (phone, ddi) => {
      if (!phone) return null
      let clean = phone.replace(/\D/g, '')
      const cleanDDI = ddi.replace(/\D/g, '')

      if (cleanDDI === '55' && clean.length === 11 && clean.charAt(2) === '9') {
        clean = clean.substring(0, 2) + clean.substring(3)
      }
      return clean
    }

    const companyData = {
      id: values.id,
      name: values.name,
      email: values.email,
      phone: formatPhone(values.phone, companyCountryCode),
      countryCode: companyCountryCode
        ? companyCountryCode.replace(/\D/g, '')
        : null,
      resposavel: values.resposavel,
      planId: values.planId,
      invoiceValue: values.invoiceValue,
      dueDate: values.dueDate === '' ? null : values.dueDate,
      recurrence: values.recurrence,
      status: values.status,
      campaignsEnabled: values.campaignsEnabled,

      adminName: values.adminName,
      adminEmail: values.adminEmail,
      adminPhone: values.adminPhone,
      adminCountryCode: adminCountryCode
        ? adminCountryCode.replace(/\D/g, '')
        : '55',
      adminPassword: values.adminPassword
    }
    console.log('companyData', companyData)

    try {
      const companyResponse = await onSubmit(companyData)
      if (values.id !== undefined) return

      const newCompanyId = companyResponse?.data?.id || companyResponse?.id
      if (!newCompanyId) {
        throw new Error('Não foi possível recuperar o ID da empresa criada.')
      }

      const adminUserData = {
        name: values.adminName,
        email: values.adminEmail,
        password: values.adminPassword,
        telefone: formatPhone(values.adminPhone, adminCountryCode),
        countryCode: adminCountryCode
          ? adminCountryCode.replace(/\D/g, '')
          : null,
        profile: 'admin',
        companyId: newCompanyId,
        queueIds: []
      }

      await api.post('/users', adminUserData)
      setConfirmPassword('')
    } catch (err) {
      const errorCode = err?.response?.data?.error
      const existingCompany = err?.response?.data?.details?.existingCompany

      if (
        (errorCode === 'ERR_EMAIL_IN_USE' ||
          errorCode === 'ERR_COMPANY_EMAIL_ALREADY_EXISTS' ||
          errorCode === 'ERR_COMPANY_NAME_ALREADY_EXISTS') &&
        existingCompany
      ) {
        if (onEmailInUse) {
          const modalTitle =
            errorCode === 'ERR_COMPANY_NAME_ALREADY_EXISTS'
              ? 'Nome já em uso'
              : errorCode === 'ERR_COMPANY_EMAIL_ALREADY_EXISTS'
                ? 'E-mail comercial já em uso'
                : 'E-mail já em uso'
          onEmailInUse(existingCompany, modalTitle)
        }
        throw err
      }

      toastError(err)
      throw err
    }
  }

  const handlePlanChange = useCallback(
    (planId, setFieldValue) => {
      setFieldValue('planId', planId)
      const selectedPlan = plans.find(
        plan => String(plan.id) === String(planId)
      )
      if (selectedPlan && selectedPlan.value) {
        setFieldValue('invoiceValue', selectedPlan.value)
      } else {
        setFieldValue('invoiceValue', '')
      }
    },
    [plans]
  )

  const handleSendLink = async () => {
    setSendingLink(true)
    try {
      await onSendAccessLink(initialValue?.adminEmail)
    } finally {
      setSendingLink(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="company-modal-title"
    >
      <DialogTitle id="company-modal-title" className={classes.dialogTitle}>
        <Typography variant="h6" component="span">
          {record.id !== undefined
            ? 'Editar Empresa'
            : 'Cadastrar Nova Empresa'}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.description}
        >
          Preencha os dados da empresa e do usuário administrador.
        </Typography>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={record}
        validationSchema={companyValidationSchema}
        onSubmit={(values, { resetForm }) => {
          return handleSubmit(values)
            .then(() => {
              resetForm()
            })
            .catch(() => {
              // Bloqueia o reset involuntário do formulário se houver falhas manuais
            })
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          resetForm,
          submitForm
        }) => (
          <>
            <DialogContent dividers component="section">
              <Form className={classes.fullWidth} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle2"
                      className={classes.sectionTitle}
                      component="h3"
                    >
                      Dados da Empresa
                    </Typography>
                  </Grid>

                  <Grid item xs={12} className={classes.inputWrapper}>
                    <Grid item xs={7}>
                      <InputField
                        label="Nome da empresa"
                        name="name"
                        value={values.name || ''}
                        onChange={e => setFieldValue('name', e.target.value)}
                        required
                        placeholder="Nome da Empresa LTDA"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <InputField
                        label="E-mail da empresa"
                        name="email"
                        type="email"
                        value={values.email || ''}
                        onChange={e => setFieldValue('email', e.target.value)}
                        fullWidth
                        required
                        placeholder="empresa@dominio.com.br"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} className={classes.inputWrapper}>
                    <Grid item xs={5}>
                      <UserPhoneCountry
                        label="Telefone"
                        value={`+${companyCountryCode.replace(/\D/g, '')}${values.phone || ''}`}
                        onChange={data => {
                          setCompanyCountryCode(`+${data.countryCode}`)
                          setFieldValue('phone', data.telefone)
                        }}
                      />
                    </Grid>
                    <Grid item xs={7}>
                      <InputField
                        label="Nome do responsável"
                        name="resposavel"
                        value={values.resposavel || ''}
                        onChange={e =>
                          setFieldValue('resposavel', e.target.value)
                        }
                        fullWidth
                        required
                        placeholder="Responsável pela Empresa"
                        error={touched.resposavel && Boolean(errors.resposavel)}
                        helperText={touched.resposavel && errors.resposavel}
                        inputProps={{ 'aria-required': true }}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} className={classes.inputWrapper}>
                    <Grid item xs={6}>
                      <SelectInput
                        labelId="plan-selection-label"
                        id="plan-selection"
                        label="Plano"
                        name="planId"
                        value={values.planId || ''}
                        onChange={e =>
                          handlePlanChange(e.target.value, setFieldValue)
                        }
                        error={touched.planId && Boolean(errors.planId)}
                        helperText={touched.planId && errors.planId}
                      >
                        <MenuItem value="">Selecione</MenuItem>
                        {plans.map(plan => (
                          <MenuItem key={plan.id} value={plan.id}>
                            {plan.name}
                          </MenuItem>
                        ))}
                      </SelectInput>
                    </Grid>
                    <Grid item xs={6}>
                      <SelectInput
                        labelId="recorrencia-selection-label"
                        id="recurrence"
                        label="Recorrência"
                        name="recurrence"
                        value={values.recurrence || ''}
                        onChange={e =>
                          setFieldValue('recurrence', e.target.value)
                        }
                        error={touched.recurrence && Boolean(errors.recurrence)}
                        helperText={touched.recurrence && errors.recurrence}
                      >
                        <MenuItem value="">Selecione</MenuItem>
                        <MenuItem value="MENSAL">Mensal</MenuItem>
                        <MenuItem value="BIMESTRAL">Bimestral</MenuItem>
                        <MenuItem value="TRIMESTRAL">Trimestral</MenuItem>
                        <MenuItem value="SEMESTRAL">Semestral</MenuItem>
                        <MenuItem value="ANUAL">Anual</MenuItem>
                      </SelectInput>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} className={classes.inputWrapper}>
                    <Grid item xs={6}>
                      <InputFieldText
                        label="Valor Mensal"
                        name="invoiceValue"
                        value={formatToBRL(values.invoiceValue)}
                        variant="outlined"
                        disabled
                        fullWidth
                        placeholder="R$ 0,00"
                        className={classes.inputField || ''}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <InputFieldText
                        label="Data de vencimento"
                        type="date"
                        name="dueDate"
                        value={values.dueDate || ''}
                        onChange={e => setFieldValue('dueDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        required
                        error={touched.dueDate && Boolean(errors.dueDate)}
                        helperText={touched.dueDate && errors.dueDate}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={6}>
                    <div className={classes.boxSwitch}>
                      <span>Ativo</span>
                      <InputSwitch
                        label="Define se a empresa poderá acessar o sistema"
                        checked={!!values.status}
                        onChange={e =>
                          setFieldValue('status', e.target.checked)
                        }
                        color="primary"
                      />
                    </div>
                  </Grid>

                  <Grid item xs={6}>
                    <div className={classes.boxSwitch}>
                      <span>Módulo de Campanhas</span>
                      <InputSwitch
                        label="Permite liberar ou bloquear o módulo de campanhas"
                        checked={!!values.campaignsEnabled}
                        onChange={e =>
                          setFieldValue('campaignsEnabled', e.target.checked)
                        }
                        color="primary"
                      />
                    </div>
                  </Grid>

                  <Grid item xs={12}>
                    <Box mt={2}>
                      <div className={classes.contentTitleUser}>
                        <Typography
                          variant="subtitle2"
                          className={classes.sectionTitle}
                          component="h3"
                        >
                          Usuário Administrador
                        </Typography>
                        {initialValue?.id && (
                          <Button
                            startIcon={<MailCheck size={16} />}
                            onClick={handleSendLink}
                            disabled={loading || sendingLink}
                            size="small"
                            aria-label="Enviar link de acesso para o administrador"
                            className={classes.sendLinkButton}
                          >
                            Enviar Link
                          </Button>
                        )}
                      </div>
                      <Typography
                        variant="body2"
                        component="span"
                        color="textSecondary"
                        className={classes.description}
                      >
                        Esse usuário será criado como administrador da empresa e
                        poderá acessar o sistema.
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} className={classes.inputWrapper}>
                    <Grid item xs={6}>
                      <InputField
                        label="Nome do administrador"
                        name="adminName"
                        value={values.adminName || ''}
                        onChange={e =>
                          setFieldValue('adminName', e.target.value)
                        }
                        fullWidth
                        required
                        placeholder="Nome Completo do Admin"
                        error={touched.adminName && Boolean(errors.adminName)}
                        helperText={touched.adminName && errors.adminName}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <InputField
                        label="E-mail do administrador"
                        name="adminEmail"
                        type="email"
                        value={values.adminEmail || ''}
                        onChange={e =>
                          setFieldValue('adminEmail', e.target.value)
                        }
                        fullWidth
                        required
                        placeholder="admin@empresa.com.br"
                        error={touched.adminEmail && Boolean(errors.adminEmail)}
                        helperText={touched.adminEmail && errors.adminEmail}
                      />
                    </Grid>
                  </Grid>

                  <Grid item xs={12} className={classes.inputWrapper}>
                    <Grid item xs={4}>
                      <InputField
                        label="Senha"
                        name="adminPassword"
                        type={showAdminPassword ? 'text' : 'password'} // Alterna dinamicamente o tipo do input
                        value={values.adminPassword || ''}
                        placeholder="••••••"
                        onChange={e =>
                          setFieldValue('adminPassword', e.target.value)
                        }
                        fullWidth
                        required
                        error={
                          touched.adminPassword && Boolean(errors.adminPassword)
                        }
                        helperText={
                          touched.adminPassword && errors.adminPassword
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="Alternar visibilidade da senha do administrador"
                                onClick={() =>
                                  setShowAdminPassword(prev => !prev)
                                }
                                edge="end"
                              >
                                {showAdminPassword ? (
                                  <EyeOff size={18} />
                                ) : (
                                  <Eye size={18} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <InputField
                        label="Confirmar Senha"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'} // Alterna dinamicamente o tipo do input
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        fullWidth
                        required
                        placeholder="••••••"
                        className={classes.inputField || ''}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="Alternar visibilidade da confirmação de senha"
                                onClick={() =>
                                  setShowConfirmPassword(prev => !prev)
                                }
                                edge="end"
                              >
                                {showConfirmPassword ? (
                                  <EyeOff size={18} />
                                ) : (
                                  <Eye size={18} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <UserPhoneCountry
                        label="Telefone do Admin"
                        value={`+${adminCountryCode.replace(/\D/g, '')}${values.adminPhone || ''}`}
                        onChange={data => {
                          setAdminCountryCode(`+${data.countryCode}`)
                          setFieldValue('adminPhone', data.telefone)
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Form>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
              <div>
                {values.id !== undefined && (
                  <IconButton
                    disabled={loading}
                    color="secondary"
                    className={classes.iconButtonDelete}
                    onClick={() => onDelete(values)}
                  >
                    <Trash2 />
                  </IconButton>
                )}
              </div>
              <div className={classes.actionButtons}>
                <ButtonSecondary
                  disabled={loading}
                  onClick={() => {
                    resetForm()
                    onClose()
                  }}
                  label="Cancelar"
                />
                <ButtonPrimary
                  disabled={loading}
                  onClick={submitForm}
                  label="Salvar"
                />
              </div>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  )
}
